import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Tabs,
  Tab,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Autocomplete,
  Chip,
  TablePagination,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_PORTAL_ADMIN_API_URL;

const PortalManager = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [topics, setTopics] = useState([]);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [newTopics, setNewTopics] = useState([]);
  const [accessAllAreas, setAccessAllAreas] = useState(false);

  // Pagination states for logs
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  useEffect(() => {
    fetchUsers();
    fetchLogs();
    fetchTopics();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/users`);
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API_URL}/logs`);
      setLogs(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTopics = async () => {
    try {
      const res = await axios.get(`${API_URL}/topics`);
      setTopics(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenCreate = () => {
    setEditUser(null);
    setNewEmail('');
    setNewTopics([]);
    setAccessAllAreas(false);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (user) => {
    setEditUser(user);
    setNewEmail(user.Email || user.Username || '');
    const isAll = user.AllowedTopics?.includes('all') || false;
    setAccessAllAreas(isAll);
    setNewTopics(isAll ? [] : (user.AllowedTopics || []));
    setCreateDialogOpen(true);
  };

  const handleCreateOrUpdateUser = async () => {
    try {
      const topicsToSave = accessAllAreas ? ['all'] : newTopics;
      await axios.post(`${API_URL}/users`, {
        email: newEmail,
        topics: topicsToSave,
        prefixes: ['reports/']
      });
      setCreateDialogOpen(false);
      setNewEmail('');
      setNewTopics([]);
      setAccessAllAreas(false);
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      console.error(err);
      alert(editUser ? 'Error updating user' : 'Error creating user');
    }
  };

  const handleDeleteUser = async (sub) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API_URL}/users/${sub}`);
      fetchUsers();
    } catch (err) {
      console.error(err);
      alert('Error deleting user');
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>Portal Manager</Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, val) => setSelectedTab(val)}>
          <Tab label="Users" />
          <Tab label="Activity Logs" />
        </Tabs>
      </Box>

      {selectedTab === 0 && (
        <Box>
          <Button variant="contained" color="primary" onClick={handleOpenCreate} sx={{ mb: 2 }}>
            Create New User
          </Button>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Allowed Topics</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.Sub || user.Username}>
                    <TableCell>{user.Email}</TableCell>
                    <TableCell>{user.UserStatus}</TableCell>
                    <TableCell>{new Date(user.UserCreateDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {user.AllowedTopics?.includes('all') ? (
                        <Chip label="Access All Areas" color="primary" size="small" />
                      ) : (
                        user.AllowedTopics?.map(t => <Chip key={t} label={t} size="small" sx={{ mr: 0.5 }} />)
                      )}
                    </TableCell>
                    <TableCell>
                      <Button color="primary" onClick={() => handleOpenEdit(user)} sx={{ mr: 1 }}>
                        <EditIcon />
                      </Button>
                      <Button color="error" onClick={() => handleDeleteUser(user.Sub || user.Username)}>
                        <DeleteIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Report</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log) => {
                  let timestamp = log.LogID;
                  if (log.Timestamp) {
                    timestamp = new Date(log.Timestamp).toLocaleString();
                  } else {
                    const parts = log.LogID?.split('_') || [];
                    if (parts.length >= 2) {
                      timestamp = `${parts[0]} ${parts[1].replace(/-/g, ':')}`;
                    }
                  }
                  
                  const userObj = users.find(u => (u.Sub === log.CognitoUserID) || (u.Username === log.CognitoUserID));
                  const userDisplay = userObj ? userObj.Email : (log.CognitoUserID || log.Email || 'Unknown');
                  
                  return (
                    <TableRow key={log.LogID}>
                      <TableCell>{timestamp}</TableCell>
                      <TableCell>{userDisplay}</TableCell>
                      <TableCell>{log.Action || 'View'}</TableCell>
                      <TableCell>{log.ReportURL || log.ReportPath || log.Action}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={logs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      )}

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editUser ? 'Edit Portal User' : 'Create New Portal User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email Address"
              fullWidth
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={!!editUser}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={accessAllAreas}
                  onChange={(e) => setAccessAllAreas(e.target.checked)}
                  color="primary"
                />
              }
              label="Access All Areas (Wildcard entitlement)"
            />
            {!accessAllAreas && (
              <Autocomplete
                multiple
                options={topics.filter(t => t !== 'all')}
                value={newTopics}
                onChange={(e, newValue) => setNewTopics(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Allowed Topics" placeholder="Topics" />
                )}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateOrUpdateUser} variant="contained" disabled={!newEmail}>
            {editUser ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalManager;
